import { Button } from "@/components/ui/button";
import { FileDown, FileSpreadsheet, Loader2, RotateCcw, Search } from "lucide-react";
import type { ReactNode } from "react";

type AccountReportFilterActionsProps = {
  statusText: ReactNode;
  hasApplied: boolean;
  loading: boolean;
  exporting?: "pdf" | "excel" | null;
  onApply: () => void;
  onReset: () => void;
  onExport?: (format: "pdf" | "excel") => void;
  showExport?: boolean;
  applyLabel?: string;
};

export function AccountReportFilterActions({
  statusText,
  hasApplied,
  loading,
  exporting = null,
  onApply,
  onReset,
  onExport,
  showExport = true,
  applyLabel = "Apply filter",
}: AccountReportFilterActionsProps) {
  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
      <p className="text-xs text-muted-foreground">{statusText}</p>
      <div className="flex items-center gap-2">
        {showExport && onExport ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={!hasApplied || loading || exporting != null}
              onClick={() => onExport("pdf")}
            >
              {exporting === "pdf" ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-3.5 w-3.5" />
              )}
              PDF
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={!hasApplied || loading || exporting != null}
              onClick={() => onExport("excel")}
            >
              {exporting === "excel" ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
              )}
              Excel
            </Button>
          </>
        ) : null}
        <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={onReset}>
          <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
          Reset
        </Button>
        <Button
          type="button"
          variant="premium"
          size="sm"
          className="h-8 text-xs"
          onClick={onApply}
          disabled={loading}
        >
          {loading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Search className="mr-1.5 h-3.5 w-3.5" />}
          {applyLabel}
        </Button>
      </div>
    </div>
  );
}

type AccountReportTableShellProps = {
  loading: boolean;
  hasApplied: boolean;
  notAppliedMessage: string;
  children: ReactNode;
};

export function AccountReportTableShell({
  loading,
  hasApplied,
  notAppliedMessage,
  children,
}: AccountReportTableShellProps) {
  return (
    <div className="overflow-hidden rounded-2xl border border-border/55 bg-card/80 shadow-sm ring-1 ring-border/25">
      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !hasApplied ? (
        <p className="py-12 text-center text-sm text-muted-foreground">{notAppliedMessage}</p>
      ) : (
        children
      )}
    </div>
  );
}

type AccountReportPageHeaderProps = {
  icon: ReactNode;
  title: string;
  description: string;
  actions?: ReactNode;
};

export function AccountReportPageHeader({ icon, title, description, actions }: AccountReportPageHeaderProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          {icon}
          <h1 className="font-display text-xl font-bold tracking-tight text-foreground">{title}</h1>
        </div>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{description}</p>
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
